import React, { useState, useEffect, useCallback, useRef, Fragment, CSSProperties, useMemo } from 'react'
import update from 'immutability-helper'
import { Table as STable, TableHeader as STableHeader, Input as SInput, Card as SCard, Label, Icon } from 'semantic-ui-react'
import { useSelector } from '../../../../hooks/useSelector'
import { Resource, GalacticTraderOffer, GalacticTrader } from '@prometheus/types'
import { translateResource, translateResourceColorRaw } from '../../../../utils/translateResource'
import { sumBy, round, isEqual } from 'lodash'
import usePlayerActions from '../../hooks/usePlayerActions'
import { SemanticCOLORS } from 'semantic-ui-react/dist/commonjs/generic'
import PromiseButton from '../../../../components/PromiseButton'
import { truncateCash } from '@prometheus/utils'
import classNames from 'classnames'
import { useForecastYears } from '../../../../hooks/useForecastYears'
import { getForecastYearOptions } from '../../../../utils/getResourceUsageForecast'
import { useResourceForecast } from '../../hooks/useResourceForecast'
import { ForecastYearPicker } from '../../../../components/ForecastYearPicker'
import { Box, ButtonProps, Card, Divider, Input, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@prometheus/components'
import PromiseButtonV2 from '@/components/PromiseButtonV2'

export const resources: (keyof Resource)[] = ['Wa', 'Cu', 'Fe', 'Ag', 'Au', 'Oil', 'Dia', 'Ti', 'Pt', 'U']

type tradeStatus =
  'empty' |
  'editing' |
  'syncing' |
  'saved' |
  'error' |
  'done'

const tradeStatusColors: Record<tradeStatus, [CSSProperties['color'], CSSProperties['color']]> = {
  done: ['green', 'white'],
  empty: ['green', 'white'],
  editing: ['orange', 'white'],
  syncing: ['black', 'white'],
  saved: ['darkgreen', 'lightgreen'],
  error: ['red', 'white'],
}

const tradeStatusText: Record<tradeStatus, string> = {
  done: 'Done',
  empty: 'Empty',
  editing: 'Editing',
  syncing: 'Syncing',
  saved: 'Saved 😊',
  error: 'Error',
}

export default () => {
  const [forecastYears, setForecastYears] = useForecastYears()
  const number_of_levels = useSelector(s => s.game.options?.planet_generation_options?.number_of_levels)
  const yearOptions = useMemo(() => getForecastYearOptions(number_of_levels), [number_of_levels])
  const trade = useSelector(s => s.game.metadata?.trade)
  const myResources = useSelector(s => s.game.sheet?.resources)
  const resourceCosts = useSelector(s => s.game.metadata?.resource_costs) || {}
  const cash = useSelector(s => s.game.sheet?.cash) || 0
  const is_map = useSelector(s => s.game.galaxy_display)
  const savedOffer = useSelector(s => s.game.sheet?.trade_offer)
  const savedOfferRef = useRef(savedOffer)
  const [dirty, setDirty] = useState<boolean>(() => savedOffer?.committed === false)
  savedOfferRef.current = savedOffer
  const players = useSelector(s => s.game.metadata?.players) || {}
  const oldTrade = useRef<GalacticTrader | undefined>(trade)
  const [tradeStatus, setTradeStatus] = useState<tradeStatus>(() => {
    if (!savedOffer) {
      return 'empty'
    }

    if (savedOffer.committed) {
      return 'done'
    }

    return 'saved'
  })

  const {
    onCommitTrade,
  } = usePlayerActions()

  const [tradeOffer, setTradeOffer] = useState<GalacticTraderOffer>(() => (savedOffer ?? {
    Ag: { quantity: 0, price: trade?.Ag.price || 0 },
    Au: { quantity: 0, price: trade?.Au.price || 0 },
    Cu: { quantity: 0, price: trade?.Cu.price || 0 },
    Dia: { quantity: 0, price: trade?.Dia.price || 0 },
    Fe: { quantity: 0, price: trade?.Fe.price || 0 },
    Oil: { quantity: 0, price: trade?.Oil.price || 0 },
    Pt: { quantity: 0, price: trade?.Pt.price || 0 },
    Ti: { quantity: 0, price: trade?.Ti.price || 0 },
    U: { quantity: 0, price: trade?.U.price || 0 },
    Wa: { quantity: 0, price: trade?.Wa.price || 0 },
  }))

  const onQuantityChange = useCallback((e) => {
    const resource = e.target.name as keyof Resource
    const value = e.target.value
    setTradeOffer(p => update(p, {
      [resource]: { quantity: { $set: round(Number(value)) } },
    }))
    setTradeStatus('editing')
    setDirty(true)
  }, [])

  const onPriceChange = useCallback((e) => {
    const resource = e.target.name as keyof Resource
    const value = e.target.value
    setTradeOffer(p => update(p, {
      [resource]: { price: { $set: round(Number(value)) } },
    }))
    setTradeStatus('editing')
    setDirty(true)
  }, [])

  const onSubmitOffer = useCallback((overrideTrade?: GalacticTraderOffer) => {
    const offer = overrideTrade ? overrideTrade : tradeOffer
    setDirty(offer.committed === false)
    return onCommitTrade(offer)
  }, [tradeOffer, onCommitTrade])

  const onResetTrade = useCallback(() => {
    if (!trade) return
    setTradeOffer({
      Ag: { quantity: 0, price: trade?.Ag.price || 0 },
      Au: { quantity: 0, price: trade?.Au.price || 0 },
      Cu: { quantity: 0, price: trade?.Cu.price || 0 },
      Dia: { quantity: 0, price: trade?.Dia.price || 0 },
      Fe: { quantity: 0, price: trade?.Fe.price || 0 },
      Oil: { quantity: 0, price: trade?.Oil.price || 0 },
      Pt: { quantity: 0, price: trade?.Pt.price || 0 },
      Ti: { quantity: 0, price: trade?.Ti.price || 0 },
      U: { quantity: 0, price: trade?.U.price || 0 },
      Wa: { quantity: 0, price: trade?.Wa.price || 0 },
      committed: false,
    })
    setTradeStatus('editing')
  }, [trade])

  // Deficit/surplus over the same window the mine popup is set to, net of what
  // the player's own mines will produce in that window.
  const { usage_forecast, production_forecast } = useResourceForecast(forecastYears)

  useEffect(() => {
    if (isEqual(trade, oldTrade.current) || !trade) {
      oldTrade.current = trade
      return
    }
    oldTrade.current = trade

    setTradeOffer(tradeOffer => {
      return update(tradeOffer, {
        Ag: { $set: { quantity: 0, price: trade.Ag.price } },
        Au: { $set: { quantity: 0, price: trade.Au.price } },
        Cu: { $set: { quantity: 0, price: trade.Cu.price } },
        Dia: { $set: { quantity: 0, price: trade.Dia.price } },
        Fe: { $set: { quantity: 0, price: trade.Fe.price } },
        Oil: { $set: { quantity: 0, price: trade.Oil.price } },
        Pt: { $set: { quantity: 0, price: trade.Pt.price } },
        Ti: { $set: { quantity: 0, price: trade.Ti.price } },
        U: { $set: { quantity: 0, price: trade.U.price } },
        Wa: { $set: { quantity: 0, price: trade.Wa.price } },
      })
    })
  }, [trade])

  const errors = useMemo(() => {
    const errors: string[] = []
    resources.forEach(r => {
      if (!trade || !myResources) {
        return
      }
      if (trade[r].option === 'sell') {
        if (tradeOffer[r].price > trade[r].price) {
          errors.push(`You must bid less than the price of the sale for <span class="${r}">${translateResource(r)}</span>`)
        }
        if (tradeOffer[r].price < 0) {
          errors.push(`You cannot bid a negative amount for <span class="${r}">${translateResource(r)}</span>`)
        }
        if (tradeOffer[r].quantity - (savedOffer?.[r]?.quantity ?? 0) > myResources[r]) {
          errors.push(`You only have <span class="${r}">${(myResources[r] + (savedOffer?.[r]?.quantity ?? 0)).toString()} ${translateResource(r)}</span> available to sell..`)
        }
      }
      if (trade[r].option === 'buy') {
        if (tradeOffer[r].price < trade[r].price) {
          errors.push(`You must bid more than the price of the sale for <span class="${r}">${translateResource(r)}</span>`)
        }
      }
      if (tradeOffer[r].quantity > trade[r].quantity) {
        errors.push(`You cannot bid for more <span class="${r}">${translateResource(r)}</span> than is available to bid for.`)
      }
    })
    const totalPrice = sumBy(resources, (r) => {
      if (!trade || !tradeOffer || !savedOffer || trade[r].option === 'sell') {
        return 0
      }

      return (tradeOffer[r].quantity - savedOffer[r].quantity) * tradeOffer[r].price
    })
    if (totalPrice !== 0 && totalPrice > cash) {
      errors.push(`You cannot afford this trade. You need an additional <span class="U">${truncateCash(Math.abs(cash - totalPrice))}</span>.`)
    }
    return errors
  }, [
    trade,
    myResources,
    savedOffer,
    tradeOffer,
    cash,
  ])

  useEffect(() => {
    if (tradeStatus !== 'editing' || errors.length) {
      return
    }
    const timeout = setTimeout(() => {
      setTradeStatus('syncing')
      onSubmitOffer({ ...tradeOffer, committed: false })
        .then(() => setTradeStatus('saved'))
        .catch(() => setTradeStatus('error'))
    }, 1000)
    return () => clearTimeout(timeout)
  }, [onSubmitOffer, tradeOffer, tradeStatus, errors])

  if (!trade || !myResources) {
    return null
  }

  function onFinalSubmit() {
    onSubmitOffer({ ...tradeOffer, committed: true })
  }

  const isEmpty = resources.every(r => tradeOffer?.[r].quantity === 0)
  let buttonText
  let buttonColor: ButtonProps['variant']
  if (savedOffer?.committed) {
    buttonText = 'Waiting for others...'
    buttonColor = 'secondary'
  } else if (errors.length) {
    buttonText = 'Review Errors'
    buttonColor = 'danger'
  } else if (isEmpty) {
    buttonText = 'Skip Offer'
    buttonColor = 'warning'
  } else if (savedOffer?.committed === false && dirty) {
    buttonText = 'Commit Prices'
    buttonColor = 'primary'
  } else {
    buttonText = 'Skip Offer'
    buttonColor = 'warning'
  }

  const bill: any[] = []
  let totalCost = 0
  let totalEarnings = 0
  for (const resource of resources) {
    const offer = tradeOffer[resource]
    const isBuy = trade[resource].option === 'buy'
    if (offer.quantity) {
      if (isBuy) {
        totalCost += offer.quantity * offer.price
      } else {
        totalEarnings += offer.quantity * offer.price
      }
      bill.push((
        <TableRow key={resource}>
          <TableCell>{isBuy ? 'Buy ' : 'Sell '}<span style={{ color: translateResourceColorRaw(resource) }}>{translateResource(resource)}</span></TableCell>
          <TableCell align="center" style={{ color: !isBuy ? 'var(--prometheus-primary)' : 'var(--prometheus-danger)' }}>{offer.quantity}</TableCell>
          <TableCell align="left">
            <span style={{ color: isBuy ? 'var(--prometheus-danger)' : 'var(--prometheus-primary)' }}>{truncateCash(offer.price)}</span> / ea</TableCell>
          <TableCell align="right" style={{ color: isBuy ? 'var(--prometheus-danger)' : 'var(--prometheus-primary)' }}>{truncateCash(offer.quantity * offer.price)}</TableCell>
        </TableRow>
      ))
    }
  }

  const statuses = Object.values(players).sort((a, b) => a.name.localeCompare(b.name)).map((p, i, a) => (
    <Fragment key={p.id}>
      <Typography color={p.trade_ready ? 'primary' : 'warning'} component="span">{p.name} - {p.trade_ready ? 'Ready!' : 'Trading'}</Typography>
      {(a.length - 1 !== i) && ' | '}
    </Fragment>
  ))

  // Sticky column styles for horizontal scrolling table
  const tableContainerStyle: CSSProperties = {
    overflowX: 'auto',
    display: 'block',
    width: 'unset'
  }

  const tableStyle: CSSProperties = {
    width: 'max-content',
  }

  const stickyHeaderCellStyle: CSSProperties = {
    position: 'sticky',
    left: 0,
    backgroundColor: 'var(--prometheus-gray-800)',
    zIndex: 10,
    width: '120px',
    borderRight: '1px solid var(--prometheus-border-light)'
  }

  const stickyCellStyle: CSSProperties = {
    position: 'sticky',
    left: 0,
    backgroundColor: 'var(--prometheus-bg-primary)',
    zIndex: 9,
    width: '120px',
    fontWeight: 'bold',
    borderRight: '1px solid var(--prometheus-border-light)'
  }

  const resourceCellStyle: CSSProperties = {
    width: '40px'
  }

  const resourceHeaderCellStyle: CSSProperties = {
    fontFamily: 'var(--prometheus-font-family-sans)',
    textTransform: 'unset',
    width: '40px'
  }

  return (
    <Card transparent className={classNames({ is_map })} padding="none" borderColor='tertiary' style={{ zIndex: 1, position: 'relative' }}>
      <Stack style={{ gap: 0 }}>
        <Typography retro variant="h2" color="tertiary" align="center">GALACTIC TRADER</Typography>
        <Divider />
        <Typography align="center">
          {statuses}
        </Typography>
        <Divider />
        {/* Same control (and same stored window) as the mine popup's. */}
        <ForecastYearPicker
          className="forecast-years--trader"
          label="Deficit/Surplus over"
          years={forecastYears}
          options={yearOptions}
          onSelect={setForecastYears}
        />
        <Divider />
        <div style={{ display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
          <Box space="sm" style={{ minWidth: 0, flexShrink: 1 }}>
            <Card padding="none" style={tableContainerStyle}>
              <Table style={tableStyle}>
                <TableHead>
                  <TableRow variant="head">
                    <TableCell align="center" variant="head" style={stickyHeaderCellStyle}></TableCell>
                    {resources.map((resource) => {
                      return (
                        <TableCell
                          key={resource}
                          align="center"
                          variant="head"
                          style={{
                            ...resourceHeaderCellStyle,
                            color: translateResourceColorRaw(resource),
                          }}
                        >{resource}</TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Quantity</TableCell>
                    {resources.map((r) => (
                      <TableCell key={r} align="center" style={{ ...resourceCellStyle, fontWeight: 'bold' }}>{truncateCash(trade[r].quantity, { raw: true })}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Avg Price</TableCell>
                    {resources.map((r) => (
                      <TableCell key={r} align="center" style={{ ...resourceCellStyle, fontStyle: 'italic' }}>{resourceCosts[r] ? truncateCash(resourceCosts[r].cash || 0) : ''}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Cost</TableCell>
                    {resources.map((r) => (
                      <TableCell key={r} align="center" style={{ ...resourceCellStyle, color: 'var(--prometheus-danger)' }}>
                        {trade[r].option === 'buy' && truncateCash(trade[r].price || 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Profit</TableCell>
                    {resources.map((r) => (
                      <TableCell key={r} align="center" style={{ ...resourceCellStyle, color: 'var(--prometheus-primary)' }}>{trade[r].option === 'sell' && truncateCash(trade[r].price || 0)}</TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>
                      No. Held
                      <br />
                      (Deficit/Surplus)
                      <br />
                      <span style={{ fontWeight: 400, fontSize: '.8em', opacity: .8 }}>
                        {forecastYears} {forecastYears === 1 ? 'yr' : 'yrs'}, net of mine production
                      </span>
                    </TableCell>
                    {resources.map(r => {
                      const amtHeld = myResources[r] + (savedOffer?.[r].quantity ?? 0)
                      const forecast = amtHeld + production_forecast[r] - usage_forecast[r]
                      let color = 'var(--prometheus-gray-700)'
                      if (forecast > 0) color = 'var(--prometheus-primary)'
                      if (forecast < 0) color = 'var(--prometheus-danger)'
                      return (
                        <TableCell key={r} align='center' style={resourceCellStyle}>
                          <span style={{ fontWeight: 900 }}>{truncateCash(amtHeld, { raw: true })}</span>
                          <br />
                          <span style={{ color }}>
                            ({truncateCash(forecast, { raw: true })})
                          </span>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Bid Quantity</TableCell>
                    {resources.map(r => {
                      const amtHeld = myResources[r] + (savedOffer?.[r].quantity ?? 0)
                      const disabled = amtHeld === 0 && trade[r].option === 'sell'
                      return (
                        <TableCell key={r} style={{
                          ...resourceCellStyle,
                          padding: '1px var(--prometheus-spacing-xs)'
                        }}>
                          <Input size="table" disabled={savedOffer?.committed || disabled} value={tradeOffer[r].quantity} onChange={onQuantityChange} name={r} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell style={stickyCellStyle}>Bid Price</TableCell>
                    {resources.map(r => {
                      const amtHeld = myResources[r] + (savedOffer?.[r].quantity ?? 0)
                      const disabled = amtHeld === 0 && trade[r].option === 'sell'
                      return (
                        <TableCell key={r} style={{
                          ...resourceCellStyle,
                          padding: '1px var(--prometheus-spacing-xs)'
                        }}>
                          <Input size="table" disabled={savedOffer?.committed || disabled} value={tradeOffer[r].price} onChange={onPriceChange} name={r} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
          </Box>
        </div>
        {!!bill.length && (
          <>
            <Divider />
            <Box space="sm" style={{ display: 'flex', justifyContent: 'center' }}>
              <Card padding="none" style={{ overflow: 'hidden' }}>
                <Box left="sm">
                  <Typography variant="h4" color="tertiary" retro>TRADE SUMMARY</Typography>
                </Box>
                <Table style={{ width: 'unset' }}>
                  <TableHead>
                    <TableRow variant="head">
                      <TableCell variant="head">Action</TableCell>
                      <TableCell variant="head" align="center">Qty</TableCell>
                      <TableCell variant="head">Price</TableCell>
                      <TableCell variant="head" align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bill}
                    {!!totalCost && (
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          Total Cost: <span style={{ color: 'var(--prometheus-danger)'}}>{truncateCash(totalCost)}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    {!!totalEarnings && (
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          Total Earnings: <span style={{ color: 'var(--prometheus-primary)'}}>{truncateCash(totalEarnings)}</span>
                        </TableCell>
                      </TableRow>
                    )}
                    {(!!totalEarnings && !!totalCost) && (
                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          Net Profit: <span style={{ color: (totalEarnings - totalCost) >= 0 ? 'var(--prometheus-primary)' : 'var(--prometheus-danger)' }}>{truncateCash(totalEarnings - totalCost)}</span>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Card>
            </Box>
          </>
        )}
        <Divider />
        <Box space="sm">
          <Stack direction="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--prometheus-spacing-sm)' }}>
            <div>
              {!savedOffer?.committed && !!errors.length && (
                <Card padding="sm" borderColor='danger'>
                  <Typography variant="h4" color="danger" style={{ lineHeight: 1.3 }} retro>ERRORS</Typography>
                  {errors.map(e => {
                    return (
                      <>
                        <p style={{ margin: 0 }} dangerouslySetInnerHTML={{ __html: e }} />
                      </>
                    )
                  })}
                </Card>
              )}
            </div>
            <Stack direction="row" space="sm">
              <PromiseButtonV2 onClick={onResetTrade} disabled={savedOffer?.committed || !['empty', 'saved', 'error', 'editing'].includes(tradeStatus)} size="sm" variant="warning" outline>Reset Trade</PromiseButtonV2>
              <PromiseButtonV2 onClick={() => onFinalSubmit()} disabled={!!errors.length || savedOffer?.committed || !['empty', 'saved'].includes(tradeStatus)} size="sm" variant={buttonColor}>{buttonText}</PromiseButtonV2>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Card>
  )
}
